import { AlignValue, Point, Series, VerticalAlignValue } from 'highcharts/highcharts';
import { BaseChartProps, LegendClickHandler } from '../types/chartProps';
import { escapeHtml } from '@opengov/reporting-types/src/escapeHtml';

// Max legend string length before "..." (limits how wide a single-line legend label can grow).
export const legendLabelMaxCharacters = 72;

const getLegendDisplayName = (series: Point | Series): string => {
  if (series.options?.custom?.displayName) {
    return series.options.custom.displayName;
  }
  return series.name ?? '';
};

export const truncateLegendLabel = (label: string): string => {
  if (label.length <= legendLabelMaxCharacters) {
    return label;
  }

  return `${label.slice(0, legendLabelMaxCharacters - 3)}...`;
};

export function getLegendLabel(this: Point | Series) {
  return escapeHtml(truncateLegendLabel(getLegendDisplayName(this)));
}

export function normalizeVerticalAlign(verticalAlign?: VerticalAlignValue): VerticalAlignValue {
  if (!verticalAlign) {
    return 'middle';
  }

  return (verticalAlign as string) === 'center' ? 'middle' : verticalAlign;
}

export function normalizeAlign(align: string | undefined): AlignValue {
  const alignValue = align ?? 'right';
  return alignValue as AlignValue;
}

export function calculateLegendYOffset(align?: AlignValue, verticalAlign?: VerticalAlignValue): number {
  const normalizedVerticalAlign = normalizeVerticalAlign(verticalAlign);
  const isTopRightLeft = (align === 'right' || align === 'left') && normalizedVerticalAlign === 'top';
  const isSmallScreen = typeof window !== 'undefined' && window.innerWidth <= 1040;
  return isSmallScreen ? 0 : isTopRightLeft ? 40 : 0;
}

export function applyLegendOptions(options: Highcharts.Options, params: BaseChartProps) {
  const verticalAlign = normalizeVerticalAlign(params.legend?.verticalAlign);
  const align = normalizeAlign(params.legend?.align);
  const yOffset = calculateLegendYOffset(align, params.legend?.verticalAlign as VerticalAlignValue | undefined);

  options.legend = {
    ...options.legend,
    align,
    verticalAlign,
    y: yOffset,
    enabled: !params.hideLegends,
    events: {
      ...options.legend?.events,
      itemClick: createLegendClickHandler(params.onLegendClick)
    },
    labelFormatter: getLegendLabel
  };
  return options;
}

export function createLegendClickHandler(onLegendClick?: LegendClickHandler) {
  return function (this: Highcharts.Legend, event: Highcharts.LegendItemClickEventObject) {
    if (onLegendClick) {
      event.preventDefault();
      onLegendClick((event.legendItem as Highcharts.Series).name);
    }
  };
}
